import type { Locale } from '../contracts/common.js'

/**
 * Time zones, chosen by city.
 *
 * Nobody knows they live in `America/Sao_Paulo`. They know they live in São
 * Paulo. Every function here exists so that a zone can be picked, checked and
 * displayed as a place and a clock reading, and never as an identifier the user
 * has to recognise.
 *
 * There is no zone library in this dependency tree on purpose. `Intl` ships a
 * current IANA database inside the runtime; a bundled table would be a copy that
 * goes stale the next time a government moves a DST boundary, and would cost
 * more to ship than every other dependency in the client combined.
 */

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

/**
 * The runtime's own spelling of a zone, or null if it does not know it.
 *
 * Necessary because IANA link names and canonical names are not agreed on
 * between runtimes, and the direction is not the one you would guess: Node 25
 * resolves `America/Argentina/Buenos_Aires` to `America/Buenos_Aires` and
 * `Asia/Kolkata` to `Asia/Calcutta` — the legacy spelling wins. Anything that
 * matches a zone by string equality has to go through here first, or it matches
 * on some runtimes and silently not on others.
 */
export function canonicalZone(zone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone
  } catch {
    return null
  }
}

/** `America/Argentina/Buenos_Aires` → `Buenos Aires`. */
export function zoneCity(zone: string): string {
  return (zone.split('/').pop() ?? zone).replaceAll('_', ' ')
}

/** `America/Argentina/Buenos_Aires` → `America/Argentina`. The disambiguator. */
export function zoneRegion(zone: string): string {
  const segments = zone.split('/')
  return segments.slice(0, -1).join('/').replaceAll('_', ' ')
}

/**
 * Search terms beyond the zone's own spelling.
 *
 * Ana types `Londres`, not `London` — the IANA name is in English and her
 * working language is not. Only destinations a Brazilian agency actually books
 * are listed: a table that tries to be complete is a table nobody maintains.
 * A city missing from here is still findable by its IANA spelling, so being
 * wrong about what belongs costs discoverability, never access.
 */
const SEARCH_ALIASES: Readonly<Record<string, readonly string[]>> = {
  'America/Sao_Paulo': ['sao paulo', 'são paulo', 'brasil', 'brazil', 'brasilia', 'brasília', 'rio', 'rio de janeiro'],
  'America/Bahia': ['salvador', 'bahia'],
  'America/Fortaleza': ['fortaleza', 'recife', 'natal'],
  'America/Manaus': ['manaus', 'amazonas'],
  'America/Argentina/Buenos_Aires': ['buenos aires', 'argentina'],
  'America/Santiago': ['santiago', 'chile'],
  'America/Lima': ['lima', 'peru', 'cusco', 'machu picchu'],
  'America/Bogota': ['bogota', 'bogotá', 'colombia', 'cartagena'],
  'America/Montevideo': ['montevideo', 'montevidéu', 'uruguai', 'uruguay'],
  'America/Mexico_City': ['mexico city', 'cidade do mexico', 'cidade do méxico', 'mexico', 'méxico', 'cancun', 'cancún'],
  'America/New_York': ['new york', 'nova york', 'nova iorque', 'orlando', 'miami', 'boston', 'washington', 'eua', 'usa'],
  'America/Chicago': ['chicago', 'dallas', 'houston', 'new orleans'],
  'America/Denver': ['denver', 'colorado'],
  'America/Los_Angeles': ['los angeles', 'san francisco', 'las vegas', 'california', 'califórnia'],
  'America/Toronto': ['toronto', 'canada', 'canadá', 'montreal'],
  'America/Vancouver': ['vancouver'],
  'Europe/Lisbon': ['lisboa', 'lisbon', 'portugal', 'porto'],
  'Europe/Madrid': ['madrid', 'madri', 'espanha', 'spain', 'barcelona', 'sevilha', 'seville'],
  'Europe/Paris': ['paris', 'franca', 'frança', 'france', 'nice', 'lyon'],
  'Europe/London': ['london', 'londres', 'inglaterra', 'england', 'reino unido', 'uk', 'edinburgh', 'edimburgo'],
  'Europe/Rome': ['rome', 'roma', 'italia', 'itália', 'italy', 'milao', 'milão', 'milan', 'veneza', 'venice', 'florenca', 'florença', 'florence'],
  'Europe/Amsterdam': ['amsterdam', 'amsterda', 'amsterdã', 'holanda', 'netherlands'],
  'Europe/Berlin': ['berlin', 'berlim', 'alemanha', 'germany', 'munique', 'munich'],
  'Europe/Zurich': ['zurich', 'zurique', 'suica', 'suíça', 'switzerland', 'geneva', 'genebra'],
  'Europe/Vienna': ['vienna', 'viena', 'austria', 'áustria'],
  'Europe/Prague': ['prague', 'praga', 'chequia', 'czech'],
  'Europe/Athens': ['athens', 'atenas', 'grecia', 'grécia', 'greece', 'santorini', 'mykonos'],
  'Europe/Istanbul': ['istanbul', 'istambul', 'turquia', 'turkey'],
  'Europe/Dublin': ['dublin', 'irlanda', 'ireland'],
  'Europe/Copenhagen': ['copenhagen', 'copenhague', 'dinamarca', 'denmark'],
  'Europe/Stockholm': ['stockholm', 'estocolmo', 'suecia', 'suécia', 'sweden'],
  'Europe/Oslo': ['oslo', 'noruega', 'norway'],
  'Europe/Budapest': ['budapest', 'budapeste', 'hungria', 'hungary'],
  'Europe/Warsaw': ['warsaw', 'varsovia', 'varsóvia', 'polonia', 'polônia', 'poland'],
  'Europe/Brussels': ['brussels', 'bruxelas', 'belgica', 'bélgica', 'belgium'],
  'Africa/Cairo': ['cairo', 'egito', 'egypt'],
  'Africa/Johannesburg': ['johannesburg', 'joanesburgo', 'africa do sul', 'áfrica do sul', 'south africa', 'cape town', 'cidade do cabo'],
  'Africa/Casablanca': ['casablanca', 'marrocos', 'morocco', 'marrakech'],
  'Asia/Dubai': ['dubai', 'emirados', 'emirates', 'abu dhabi'],
  'Asia/Tokyo': ['tokyo', 'toquio', 'tóquio', 'japao', 'japão', 'japan', 'kyoto', 'quioto'],
  'Asia/Shanghai': ['shanghai', 'xangai', 'china', 'pequim', 'beijing'],
  'Asia/Hong_Kong': ['hong kong'],
  'Asia/Singapore': ['singapore', 'singapura'],
  'Asia/Bangkok': ['bangkok', 'bangcoc', 'tailandia', 'tailândia', 'thailand'],
  'Asia/Seoul': ['seoul', 'seul', 'coreia', 'korea'],
  'Asia/Kolkata': ['india', 'índia', 'delhi', 'mumbai', 'calcutá'],
  'Asia/Jerusalem': ['jerusalem', 'jerusalém', 'israel', 'tel aviv'],
  'Australia/Sydney': ['sydney', 'australia', 'austrália'],
  'Pacific/Auckland': ['auckland', 'nova zelandia', 'nova zelândia', 'new zealand'],
}

/**
 * How a city is spelled, for the destinations this agency works in.
 *
 * The IANA identifier is not a name: it is ASCII-only, English, and sometimes
 * decades out of date. Deriving the label from it gives a Brazilian advisor
 * "Sao Paulo" for her own city and "Lisbon" for one she would call Lisboa —
 * which reads as a bug even when the choice underneath is correct.
 *
 * Only curated destinations appear. Everywhere else falls back to the derived
 * name, which is imperfect and still recognisable.
 */
const ZONE_NAMES: Readonly<Record<string, { readonly 'pt-BR': string; readonly en: string }>> = {
  'America/Sao_Paulo': { 'pt-BR': 'São Paulo', en: 'São Paulo' },
  'America/Bahia': { 'pt-BR': 'Salvador', en: 'Salvador' },
  'America/Fortaleza': { 'pt-BR': 'Fortaleza', en: 'Fortaleza' },
  'America/Manaus': { 'pt-BR': 'Manaus', en: 'Manaus' },
  'America/Argentina/Buenos_Aires': { 'pt-BR': 'Buenos Aires', en: 'Buenos Aires' },
  'America/Bogota': { 'pt-BR': 'Bogotá', en: 'Bogotá' },
  'America/Mexico_City': { 'pt-BR': 'Cidade do México', en: 'Mexico City' },
  'America/Montevideo': { 'pt-BR': 'Montevidéu', en: 'Montevideo' },
  'America/New_York': { 'pt-BR': 'Nova York', en: 'New York' },
  'America/Los_Angeles': { 'pt-BR': 'Los Angeles', en: 'Los Angeles' },
  'America/Toronto': { 'pt-BR': 'Toronto', en: 'Toronto' },
  'Europe/Lisbon': { 'pt-BR': 'Lisboa', en: 'Lisbon' },
  'Europe/Madrid': { 'pt-BR': 'Madri', en: 'Madrid' },
  'Europe/London': { 'pt-BR': 'Londres', en: 'London' },
  'Europe/Rome': { 'pt-BR': 'Roma', en: 'Rome' },
  'Europe/Berlin': { 'pt-BR': 'Berlim', en: 'Berlin' },
  'Europe/Zurich': { 'pt-BR': 'Zurique', en: 'Zurich' },
  'Europe/Vienna': { 'pt-BR': 'Viena', en: 'Vienna' },
  'Europe/Prague': { 'pt-BR': 'Praga', en: 'Prague' },
  'Europe/Athens': { 'pt-BR': 'Atenas', en: 'Athens' },
  'Europe/Istanbul': { 'pt-BR': 'Istambul', en: 'Istanbul' },
  'Europe/Copenhagen': { 'pt-BR': 'Copenhague', en: 'Copenhagen' },
  'Europe/Stockholm': { 'pt-BR': 'Estocolmo', en: 'Stockholm' },
  'Europe/Budapest': { 'pt-BR': 'Budapeste', en: 'Budapest' },
  'Europe/Warsaw': { 'pt-BR': 'Varsóvia', en: 'Warsaw' },
  'Europe/Brussels': { 'pt-BR': 'Bruxelas', en: 'Brussels' },
  'Europe/Moscow': { 'pt-BR': 'Moscou', en: 'Moscow' },
  'Africa/Cairo': { 'pt-BR': 'Cairo', en: 'Cairo' },
  'Africa/Johannesburg': { 'pt-BR': 'Joanesburgo', en: 'Johannesburg' },
  'Asia/Tokyo': { 'pt-BR': 'Tóquio', en: 'Tokyo' },
  'Asia/Shanghai': { 'pt-BR': 'Xangai', en: 'Shanghai' },
  'Asia/Singapore': { 'pt-BR': 'Singapura', en: 'Singapore' },
  'Asia/Bangkok': { 'pt-BR': 'Bangcoc', en: 'Bangkok' },
  'Asia/Seoul': { 'pt-BR': 'Seul', en: 'Seoul' },
  'Asia/Kolkata': { 'pt-BR': 'Calcutá', en: 'Kolkata' },
  'Asia/Jerusalem': { 'pt-BR': 'Jerusalém', en: 'Jerusalem' },
  'Australia/Sydney': { 'pt-BR': 'Sydney', en: 'Sydney' },
  'Pacific/Auckland': { 'pt-BR': 'Auckland', en: 'Auckland' },
}

const NAMES_BY_CANONICAL: ReadonlyMap<string, { readonly 'pt-BR': string; readonly en: string }> =
  new Map(
    Object.entries(ZONE_NAMES).flatMap(([zone, names]) => {
      const canonical = canonicalZone(zone)
      return canonical ? [[canonical, names] as const] : []
    }),
  )

/**
 * The city, spelled for the reader. Falls back to the identifier's own spelling.
 *
 * This is what the UI shows. `zoneCity` remains the mechanical derivation, and
 * is what search matches against — a city findable by typing its English name
 * should stay findable after being relabelled.
 */
export function zoneCityLabel(zone: string, locale: Locale): string {
  const canonical = canonicalZone(zone) ?? zone
  return NAMES_BY_CANONICAL.get(canonical)?.[locale] ?? zoneCity(zone)
}

/**
 * Aliases keyed by whatever this runtime calls each zone.
 *
 * Built once at load rather than written down, because the table above is
 * spelled the way a person would write it and the runtime may disagree — an
 * alias filed under a name the zone list never produces is an alias that never
 * matches. Two spellings that canonicalise together merge.
 */
const ALIASES_BY_CANONICAL: ReadonlyMap<string, readonly string[]> = (() => {
  const map = new Map<string, string[]>()
  for (const [zone, aliases] of Object.entries(SEARCH_ALIASES)) {
    const canonical = canonicalZone(zone)
    if (!canonical) continue
    const existing = map.get(canonical)
    if (existing) existing.push(...aliases)
    else map.set(canonical, [...aliases])
  }
  return map
})()

/**
 * Shown before anything is typed, so the common case is one tap rather than a
 * search. Ordered by how often this agency books them, not alphabetically.
 *
 * Canonicalised for the same reason the aliases are, and filtered rather than
 * trusted: a suggestion the runtime does not recognise would render a button
 * that shows no time and stores a zone nothing can format.
 */
export const SUGGESTED_ZONES: readonly string[] = [
  'America/Sao_Paulo',
  'Europe/Paris',
  'Europe/Lisbon',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/London',
  'America/New_York',
  'America/Santiago',
  'America/Argentina/Buenos_Aires',
  'Asia/Tokyo',
]
  .map(canonicalZone)
  .filter((zone): zone is string => zone !== null)

// ---------------------------------------------------------------------------
// Listing and search
// ---------------------------------------------------------------------------

export interface ZoneChoice {
  zone: string
  city: string
  region: string
}

/**
 * Every zone the runtime knows, minus the ones that are not places.
 *
 * `Etc/GMT+3` and friends are filtered out: they are offsets wearing a zone's
 * clothes, they do not observe DST, and their signs are inverted from what
 * anyone expects. A trip is in a place.
 */
export function listZones(): ZoneChoice[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []

  const zones = supported.length > 0 ? supported : [...ALIASES_BY_CANONICAL.keys()]

  return zones
    .filter((zone) => zone.includes('/') && !zone.startsWith('Etc/'))
    .map((zone) => ({ zone, city: zoneCity(zone), region: zoneRegion(zone) }))
}

/** Lower-cased and stripped of accents, so `sao paulo` finds `São Paulo`. */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}

/**
 * Ranked matches for what the user typed.
 *
 * A prefix beats a substring, and an aliased destination beats an unaliased one
 * at the same kind of match. Both rules exist for the same query: `par` is a
 * prefix of Paris and of Paramaribo, and alphabetical order puts Paramaribo
 * first. The alias table is the list of places this agency actually books, so
 * it is the honest tie-break — and being absent from it costs a place its
 * ranking, never its findability.
 */
export function searchZones(query: string, zones: ZoneChoice[] = listZones()): ZoneChoice[] {
  const needle = fold(query)
  if (!needle) return zones

  const scored: Array<{ choice: ZoneChoice; score: number }> = []

  for (const choice of zones) {
    const city = fold(choice.city)
    const names = NAMES_BY_CANONICAL.get(choice.zone)
    // Display names are searched as well as shown. A city relabelled `Lisboa`
    // that could only be found by typing `Lisbon` would be a worse control than
    // the one this replaced.
    const terms = [
      ...(ALIASES_BY_CANONICAL.get(choice.zone) ?? []),
      ...(names ? [names['pt-BR'], names.en] : []),
    ].map(fold)

    let score = 0
    if (terms.some((term) => term.startsWith(needle))) score = 4
    else if (city.startsWith(needle)) score = 3
    else if (terms.some((term) => term.includes(needle))) score = 2.5
    else if (city.includes(needle)) score = 2
    else if (fold(choice.region).includes(needle)) score = 1

    if (score > 0) scored.push({ choice, score })
  }

  return scored
    .sort((a, b) => b.score - a.score || a.choice.city.localeCompare(b.choice.city))
    .map((entry) => entry.choice)
}

// ---------------------------------------------------------------------------
// Offsets, and the wall-clock conversion the booking form depends on
// ---------------------------------------------------------------------------

function zoneParts(zone: string, instant: Date): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const out: Record<string, number> = {}
  for (const part of parts) {
    if (part.type !== 'literal') out[part.type] = Number(part.value)
  }
  return out
}

/**
 * How far ahead of UTC `zone` is at `instant`, in minutes. Paris in July: 120.
 *
 * Derived by asking Intl what the clock reads there and comparing, rather than
 * by consulting a table — which means it is correct across every DST rule the
 * runtime knows about, including historical ones.
 */
export function zoneOffsetMinutes(zone: string, instant: Date): number {
  const p = zoneParts(zone, instant)
  const asIfUtc = Date.UTC(
    p.year!,
    p.month! - 1,
    p.day!,
    // h23 can still yield 24 for midnight on some engines. Modulo, not a guard,
    // because the day part is already correct when it happens.
    p.hour! % 24,
    p.minute!,
    p.second!,
  )
  // Seconds are the finest unit Intl gives back, so compare at second precision.
  const truncated = Math.floor(instant.getTime() / 1000) * 1000
  return (asIfUtc - truncated) / 60_000
}

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * Turns a wall-clock reading in a named zone into the instant it names.
 *
 * `14:40 in America/Sao_Paulo` is a different moment from `14:40 in
 * Europe/Paris`, and this is the ONLY place in the codebase that decides which.
 * Before it existed the client parsed `datetime-local` with `new Date(...)`,
 * which silently means "in whatever zone this browser happens to be in" — right
 * by luck when Ana books a São Paulo departure from her desk in Brazil, wrong by
 * five hours for the Paris arrival on the very same ticket.
 *
 * Two passes, because the offset depends on the instant and the instant depends
 * on the offset. Guess with the offset at the naive reading, then re-derive at
 * the guess; a DST transition between the two makes the second pass differ, and
 * one correction is enough for every real-world rule.
 *
 * Returns null for input that is not a wall clock. The two hours a year that
 * civil time makes undecidable resolve by falling out of the arithmetic rather
 * than by asking: a time inside a spring-forward gap does not exist and lands
 * just after it, and a time in a fall-back repeat takes the second of the two,
 * on standard time. Both are stated here rather than surfaced in the UI —
 * an advisor typing a departure needs a plane time, not a dialog about DST.
 */
export function wallClockToInstant(wall: string, zone: string): Date | null {
  const match = WALL_CLOCK.exec(wall.trim())
  if (!match) return null

  const [, y, mo, d, h, mi, s] = match
  const naive = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0))
  if (!Number.isFinite(naive)) return null

  const firstGuess = naive - zoneOffsetMinutes(zone, new Date(naive)) * 60_000
  const corrected = naive - zoneOffsetMinutes(zone, new Date(firstGuess)) * 60_000
  return new Date(corrected)
}

/** The inverse: what a clock in `zone` reads at `instant`, as `YYYY-MM-DDTHH:mm`. */
export function instantToWallClock(instant: Date, zone: string): string {
  const p = zoneParts(zone, instant)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${p.year}-${pad(p.month!)}-${pad(p.day!)}T${pad(p.hour! % 24)}:${pad(p.minute!)}`
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/** The clock reading in `zone` right now — the confirmation that a pick is right. */
export function formatZoneTime(zone: string, locale: Locale, at: Date = new Date()): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: zone,
  }).format(at)
}

/**
 * A signed difference in hours between two zones, as text: `+5h`, `−3h30`.
 *
 * Uses a real minus sign rather than a hyphen because this appears next to
 * times, where a hyphen reads as a range separator.
 */
export function zoneDifferenceLabel(from: string, to: string, at: Date = new Date()): string {
  const minutes = zoneOffsetMinutes(to, at) - zoneOffsetMinutes(from, at)
  if (minutes === 0) return '±0'

  const sign = minutes > 0 ? '+' : '−'
  const abs = Math.abs(minutes)
  const hours = Math.floor(abs / 60)
  const rest = abs % 60
  return rest === 0 ? `${sign}${hours}h` : `${sign}${hours}h${String(rest).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Moving dates and times by whole days
// ---------------------------------------------------------------------------

/** `2026-07-10` + 5 → `2026-07-15`. Walked in UTC so no zone can shift the day. */
export function addDaysToIsoDate(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number]
  const moved = new Date(Date.UTC(y, m - 1, d))
  moved.setUTCDate(moved.getUTCDate() + days)
  return moved.toISOString().slice(0, 10)
}

/** Whole days from `from` to `to`; negative if `to` is earlier. */
export function daysBetweenIsoDates(from: string, to: string): number {
  const at = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
    return Date.UTC(y, m - 1, d)
  }
  return Math.round((at(to) - at(from)) / 86_400_000)
}

/**
 * Moves an instant by whole days, keeping the clock reading it had.
 *
 * NOT `instant + days * 86400000`. A trip copied from July into November crosses
 * a DST boundary, and adding fixed milliseconds moves a 09:00 museum booking to
 * 08:00 or 10:00 — an hour that nobody typed and nobody will notice until
 * somebody is standing outside a closed door. Going out through the wall clock
 * and back in through `wallClockToInstant` keeps 09:00 as 09:00 in the zone the
 * booking happens in, which is what "the same day, three months later" means.
 */
export function shiftInstantByDays(instant: Date, days: number, zone: string): Date {
  const wall = instantToWallClock(instant, zone)
  const [date, time] = wall.split('T') as [string, string]
  return wallClockToInstant(`${addDaysToIsoDate(date, days)}T${time}`, zone) ?? instant
}
